import React, {FC, useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {formatApiError} from '../../../shared/utils/apiErrorMessage'
import {KTSVG} from '../../../../_metronic/helpers'
import {useAuth} from '../../auth'
import {useCertificateDeleteToast} from '../core/useCertificateDeleteToast'
import {CategoryLibraryModal} from './CategoryLibraryModal'
import { QHSE_PERMS } from '../core/permissions'
import { isBroadFleetPickerUser } from '../core/qhseUserContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'

const API_URL = process.env.REACT_APP_API_URL

type CertificateNameItem = {
  id: number
  name: string
  categoryId?: number | null
  categoryName?: string | null
  displayOrder?: number | null
  isActive?: boolean | null
  companyGroupAdminId?: number | null
  scopeType?: 'GLOBAL' | 'COMPANY' | string
}

type CompanyGroup = {id: number; name: string}

type PageResponse<T> = {
  content?: T[]
  totalElements?: number
  number?: number
  size?: number
}

const emptyForm = {
  id: undefined as number | undefined,
  name: '',
  categoryId: '',
  isActive: true,
  scopeType: 'COMPANY' as 'GLOBAL' | 'COMPANY',
  companyGroupId: '',
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const CertificateLibraryModal: FC<{isOpen: boolean; onClose: () => void; targetType?: 'VESSEL' | 'OFFICE'}> = ({isOpen, onClose, targetType = 'VESSEL'}) => {
  const {auth, currentUser, can, isSuperAdmin} = useAuth()
  const confirmDelete = useCertificateDeleteToast(isOpen)
  const fleetPicker = isBroadFleetPickerUser(currentUser) || isSuperAdmin

  const canCreateName = isSuperAdmin || can(QHSE_PERMS.CERT_NAME_CREATE)
  const canUpdateName = isSuperAdmin || can(QHSE_PERMS.CERT_NAME_UPDATE)
  const canDeleteName = isSuperAdmin || can(QHSE_PERMS.CERT_NAME_DELETE)
  const canManageCategoryLibrary =
    isSuperAdmin || can(QHSE_PERMS.CERT_CATEGORY_READ) || can(QHSE_PERMS.CERT_NAME_READ)

  const canManageGlobalRows = isSuperAdmin || (fleetPicker && canUpdateName)
  const canEditNameRow = (isGlobal: boolean) => (isGlobal ? canManageGlobalRows : canUpdateName)
  const canDeleteNameRow = (isGlobal: boolean) => (isGlobal ? fleetPicker && canDeleteName : canDeleteName)

  const [rows, setRows] = useState<CertificateNameItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(10)
  const [loading, setLoading] = useState(false)

  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [scopeFilter, setScopeFilter] = useState<'ALL' | 'GLOBAL' | 'COMPANY'>('ALL')
  const [companyGroupIdFilter, setCompanyGroupIdFilter] = useState('')

  const [categories, setCategories] = useState<Array<{id: number; name: string}>>([])
  const [companies, setCompanies] = useState<CompanyGroup[]>([])

  const [form, setForm] = useState({...emptyForm})
  const isEditing = form.id != null
  const [showFormModal, setShowFormModal] = useState(false)
  const [showCategoryLibrary, setShowCategoryLibrary] = useState(false)

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / size)), [total, size])

  useEffect(() => {
    if (!isOpen) return
    fetchCategories()
    if (fleetPicker) fetchCompanies()
  }, [isOpen, fleetPicker])

  useEffect(() => {
    if (!isOpen || !fleetPicker) return
    if (form.scopeType === 'COMPANY') {
      fetchCategories(form.companyGroupId)
    } else {
      fetchCategories()
    }
  }, [isOpen, fleetPicker, form.scopeType, form.companyGroupId])

  useEffect(() => {
    if (!isOpen) return
    fetchLibrary()
  }, [isOpen, page, size, search, categoryFilter, scopeFilter, companyGroupIdFilter])

  const fetchCategories = async (companyGroupId?: string) => {
    try {
      const params = new URLSearchParams()
      params.append('targetType', targetType)
      if (companyGroupId) params.append('companyGroupId', companyGroupId)
      const res = await fetch(`${API_URL}/qhse/certificates/categories?${params.toString()}`, {
        headers: {Authorization: `Bearer ${auth?.auth?.jwt}`},
      })
      if (!res.ok) throw new Error('Failed to load categories')
      const data = await res.json()
      setCategories((Array.isArray(data) ? data : []).map((c: any) => ({id: c.id, name: c.name})))
    } catch (e: any) {
      toast.error('Failed to load categories')
    }
  }

  const fetchCompanies = async () => {
    try {
      const res = await fetch(`${API_URL}/company-group-admins`, {
        headers: {Authorization: `Bearer ${auth?.auth?.jwt}`},
      })
      if (!res.ok) throw new Error('Failed to load companies')
      const data = await res.json()
      setCompanies((Array.isArray(data) ? data : []).map((c: any) => ({id: c.id, name: c.name})))
    } catch (e: any) {
      toast.error('Failed to load companies')
    }
  }

  const fetchLibrary = async () => {
    if (!auth?.auth?.jwt) return
    if (fleetPicker && scopeFilter === 'COMPANY' && !companyGroupIdFilter) {
      setRows([])
      setTotal(0)
      return
    }
    setLoading(true)
    try {
      const params = new URLSearchParams()
      params.append('targetType', targetType)
      if (search.trim()) params.append('search', search.trim())
      if (categoryFilter) params.append('categoryId', categoryFilter)
      params.append('page', String(page))
      params.append('size', String(size))
      params.append('activeOnly', 'true')

      if (fleetPicker) {
        params.append('scope', scopeFilter)
        if (scopeFilter === 'COMPANY' || (scopeFilter === 'ALL' && companyGroupIdFilter)) {
          params.append('companyGroupId', companyGroupIdFilter)
        }
      } else {
        params.append('scope', 'ALL')
      }

      const res = await fetch(`${API_URL}/qhse/certificates/names/library?${params.toString()}`, {
        headers: {Authorization: `Bearer ${auth?.auth?.jwt}`},
      })
      if (!res.ok) throw new Error('Failed to load certificate names')
      const data = (await res.json()) as PageResponse<CertificateNameItem> | CertificateNameItem[]

      const content = Array.isArray(data) ? data : data.content || []
      const totalElements = Array.isArray(data) ? data.length : data.totalElements || 0

      setRows(content)
      setTotal(totalElements)
    } catch (e: any) {
      toast.error('Failed to load certificate names')
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => setForm({...emptyForm})

  const handleEdit = (row: CertificateNameItem) => {
    setForm({
      id: row.id,
      name: row.name || '',
      categoryId: row.categoryId ? String(row.categoryId) : '',
      isActive: row.isActive ?? true,
      scopeType: (row.scopeType === 'GLOBAL' ? 'GLOBAL' : 'COMPANY'),
      companyGroupId: row.companyGroupAdminId ? String(row.companyGroupAdminId) : '',
    })
    setShowFormModal(true)
  }

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error('Certificate name is required')
      return
    }
    if (!form.categoryId) {
      toast.error('Certificate category is required')
      return
    }
    if (fleetPicker && form.scopeType === 'COMPANY' && !form.companyGroupId) {
      toast.error('Company is required for company scope')
      return
    }

    const payload: any = {
      name: form.name.trim(),
      categoryId: Number(form.categoryId),
      isActive: form.isActive,
      targetType,
    }

    if (fleetPicker) {
      payload.scopeType = form.scopeType
      if (form.scopeType === 'COMPANY') {
        payload.companyGroupAdminId = Number(form.companyGroupId)
      }
    }

    try {
      const res = await fetch(
        isEditing
          ? `${API_URL}/qhse/certificates/names/${form.id}`
          : `${API_URL}/qhse/certificates/names`,
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.auth?.jwt}`,
          },
          body: JSON.stringify(payload),
        }
      )

      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err?.message || 'Save failed')
      }

      toast.success(isEditing ? 'Certificate name updated' : 'Certificate name created')
      resetForm()
      setShowFormModal(false)
      fetchLibrary()
    } catch (e: any) {
      toast.error(formatApiError(e, 'Save failed', 'save'))
    }
  }

  const handleDelete = (row: CertificateNameItem) => {
    confirmDelete(`certificate-name-${targetType}-${row.id}`, `Delete certificate name "${row.name}"?`, async () => {
      try {
        const res = await fetch(`${API_URL}/qhse/certificates/names/${row.id}`, {
          method: 'DELETE',
          headers: {Authorization: `Bearer ${auth?.auth?.jwt}`},
        })
        if (!res.ok) {
          const err = await res.json().catch(() => ({}))
          throw new Error(err?.message || 'Delete failed')
        }
        toast.success('Certificate name deleted')
        fetchLibrary()
      } catch (e: any) {
        toast.error(formatApiError(e, 'Delete failed', 'delete'))
      }
    })
  }

  const companyNameById = useMemo(() => {
    const map = new Map<number, string>()
    companies.forEach((c) => map.set(c.id, c.name))
    return map
  }, [companies])

  if (!isOpen) return null

  return (
    <div style={WRAP_STYLE} tabIndex={-1}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{
          width: '90vw',
          height: '85vh',
          maxWidth: '1400px',
          maxHeight: '900px',
        }}
      >
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
          <h5 className='modal-title text-dark m-0'>
            <KTSVG path='/media/icons/duotune/general/gen048.svg' className='svg-icon-3 me-2' />
            Certificate Library
          </h5>
          <button type='button' className='btn-close' onClick={onClose} />
        </div>

        <div className='flex-grow-1 overflow-auto px-4 py-3'>
            <div className='row g-3 align-items-end mb-5'>
              <div className='col-md-4'>
                <label className='form-label fw-semibold fs-6 mb-2 text-gray-700'>Search</label>
                <input
                  type='text'
                  className='form-control form-control-solid'
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value)
                    setPage(0)
                  }}
                  placeholder='Search certificate name...'
                />
              </div>
              <div className='col-md-3'>
                <label className='form-label fw-semibold fs-6 mb-2 text-gray-700'>Category</label>
                <SearchableSelect
                  className='form-select form-select-solid'
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value)
                    setPage(0)
                  }}
                >
                  <option value=''>All</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </SearchableSelect>
              </div>

              {fleetPicker && (
                <>
                  <div className='col-md-2'>
                    <label className='form-label fw-semibold fs-6 mb-2 text-gray-700'>Scope</label>
                    <SearchableSelect
                      className='form-select form-select-solid'
                      value={scopeFilter}
                      onChange={(e) => {
                        setScopeFilter(e.target.value as any)
                        setPage(0)
                      }}
                    >
                      <option value='ALL'>All</option>
                      <option value='GLOBAL'>Global</option>
                      <option value='COMPANY'>Company</option>
                    </SearchableSelect>
                  </div>
                  <div className='col-md-3'>
                    <label className='form-label fw-semibold fs-6 mb-2 text-gray-700'>Company</label>
                    <SearchableSelect
                      className='form-select form-select-solid'
                      value={companyGroupIdFilter}
                      onChange={(e) => {
                        setCompanyGroupIdFilter(e.target.value)
                        setPage(0)
                      }}
                      disabled={scopeFilter === 'GLOBAL'}
                    >
                      <option value=''>Select company</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                </>
              )}
              {(canCreateName || canManageCategoryLibrary) && (
                <div className='col-md-auto ms-auto d-flex gap-2'>
                  {canManageCategoryLibrary && (
                    <button
                      className='btn btn-light-primary'
                      type='button'
                      onClick={() => setShowCategoryLibrary(true)}
                    >
                      Category Library
                    </button>
                  )}
                  {canCreateName && (
                    <button
                      className='btn btn-primary'
                      type='button'
                      onClick={() => {
                        resetForm()
                        setShowFormModal(true)
                      }}
                    >
                      Add Certificate Name
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className='table-responsive'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th style={{width: '70px'}} className='text-center'>
                      SR/NO
                    </th>
                    <th>Certificate Name</th>
                    <th>Category</th>
                    <th>Scope</th>
                    {fleetPicker && <th>Company</th>}
                    <th className='text-center'>Active</th>
                    <th className='text-end'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={fleetPicker ? 7 : 6} className='text-center py-6'>
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={fleetPicker ? 7 : 6} className='text-center py-6'>
                        No certificate names found
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, idx) => {
                      const sr = page * size + idx + 1
                      const isGlobal = (row.scopeType || 'GLOBAL') === 'GLOBAL' || row.companyGroupAdminId == null
                      const canEditRow = canEditNameRow(isGlobal)
                      const canDelRow = canDeleteNameRow(isGlobal)
                      return (
                        <tr key={row.id}>
                          <td className='text-center'>{sr}</td>
                          <td>{row.name}</td>
                          <td>{row.categoryName || '-'}</td>
                          <td>{isGlobal ? 'Global' : 'Company'}</td>
                          {fleetPicker && (
                            <td>
                              {isGlobal
                                ? '-'
                                : companyNameById.get(Number(row.companyGroupAdminId)) || `#${row.companyGroupAdminId}`}
                            </td>
                          )}
                          <td className='text-center'>
                            <span className={`badge ${row.isActive ? 'bg-light-success' : 'bg-light-danger'}`}>
                              {row.isActive ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td className='text-end'>
                            <button
                              className='btn btn-icon btn-sm me-2'
                              title='Edit'
                              onClick={() => handleEdit(row)}
                              disabled={!canEditRow}
                            >
                              <KTSVG path='/media/map/edit-active.svg' />
                            </button>
                            <button
                              className='btn btn-icon btn-sm'
                              title='Delete'
                              onClick={() => handleDelete(row)}
                              disabled={!canDelRow}
                            >
                              <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3 text-danger' />
                            </button>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className='d-flex justify-content-between align-items-center pt-3'>
              <div className='d-flex align-items-center'>
                <span className='text-muted me-2'>Rows per page</span>
                <SearchableSelect
                  className='form-select form-select-solid form-select-sm'
                  style={{width: '80px'}}
                  value={size}
                  onChange={(e) => {
                    setSize(Number(e.target.value))
                    setPage(0)
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </SearchableSelect>
              </div>
              <div className='d-flex align-items-center gap-3'>
                <span className='text-muted'>
                  Page {page + 1} of {totalPages}
                </span>
                <div className='btn-group'>
                  <button
                    className='btn btn-light btn-sm'
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    Prev
                  </button>
                  <button
                    className='btn btn-light btn-sm'
                    onClick={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
                    disabled={page + 1 >= totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
        </div>

        <div className='border-top px-4 py-3 d-flex justify-content-end'>
          <button className='btn btn-light' onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {showFormModal && (
        <div
          className='modal fade show d-block'
          tabIndex={-1}
          style={{backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', inset: 0, zIndex: 1060}}
        >
          <div className='modal-dialog modal-dialog-centered' style={{maxWidth: 520}}>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title text-dark'>
                  {isEditing ? 'Edit Certificate Name' : 'Add Certificate Name'}
                </h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => {
                    resetForm()
                    setShowFormModal(false)
                  }}
                />
              </div>

              <div className='modal-body'>
                {fleetPicker && (
                  <div className='mb-4'>
                    <label className='form-label fw-semibold fs-6 mb-2 text-gray-700'>Scope</label>
                    <SearchableSelect
                      className='form-select form-select-solid'
                      value={form.scopeType}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          scopeType: e.target.value as 'GLOBAL' | 'COMPANY',
                          companyGroupId: e.target.value === 'GLOBAL' ? '' : prev.companyGroupId,
                          categoryId: '',
                        }))
                      }
                    >
                      <option value='GLOBAL'>Global</option>
                      <option value='COMPANY'>Company</option>
                    </SearchableSelect>
                  </div>
                )}

                {fleetPicker && form.scopeType === 'COMPANY' && (
                  <div className='mb-4'>
                    <label className='form-label required fw-semibold fs-6 mb-2 text-gray-700'>Company</label>
                    <SearchableSelect
                      className='form-select form-select-solid'
                      value={form.companyGroupId}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          companyGroupId: e.target.value,
                          categoryId: '',
                        }))
                      }
                    >
                      <option value=''>Select company</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                )}

                <div className='mb-4'>
                  <label className='form-label required fw-semibold fs-6 mb-2 text-gray-700'>Category</label>
                  <SearchableSelect
                    className='form-select form-select-solid'
                    value={form.categoryId}
                    onChange={(e) => setForm((prev) => ({...prev, categoryId: e.target.value}))}
                    disabled={fleetPicker && form.scopeType === 'COMPANY' && !form.companyGroupId}
                  >
                    <option value=''>
                      {fleetPicker && form.scopeType === 'COMPANY' && !form.companyGroupId
                        ? 'Select company first'
                        : 'Select Category'}
                    </option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>

                <div className='mb-4'>
                  <label className='form-label required fw-semibold fs-6 mb-2 text-gray-700'>Name</label>
                  <input
                    type='text'
                    className='form-control form-control-solid'
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({...prev, name: e.target.value}))}
                  />
                </div>

                <div className='form-check form-switch'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    checked={form.isActive}
                    onChange={(e) => setForm((prev) => ({...prev, isActive: e.target.checked}))}
                  />
                  <label className='form-check-label fw-semibold text-gray-700'>Active</label>
                </div>
              </div>

              <div className='modal-footer'>
                <button
                  className='btn btn-light'
                  type='button'
                  onClick={() => {
                    resetForm()
                    setShowFormModal(false)
                  }}
                >
                  Cancel
                </button>
                <button className='btn btn-primary' type='button' onClick={handleSubmit}>
                  {isEditing ? 'Save Changes' : 'Add Name'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <CategoryLibraryModal
        isOpen={showCategoryLibrary}
        onClose={() => {
          setShowCategoryLibrary(false)
          if (fleetPicker && form.scopeType === 'COMPANY') {
            fetchCategories(form.companyGroupId)
          } else {
            fetchCategories()
          }
        }}
        targetType={targetType}
      />
    </div>
  )
}

export {CertificateLibraryModal}
